import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type FranchiseTeacherStatus = 'candidate' | 'training' | 'working' | 'dismissed'
export type TeacherTrainingStatus = 'in_progress' | 'completed' | 'closed'
export type TeacherAttestationResult = 'passed' | 'retake' | 'refused'
export type TeacherQualification = 'stretch_pilates' | 'universal'
export type AdditionalAttestationType = 'regulation' | 'low_quality' | 'planned_recertification'
export type TeacherVideoDecision = 'approved' | 'rejected'

export interface FranchiseTeacherAssignment {
  assignmentId: string
  teacherId: string
  partnerNumber: number
  partnerName: string
  cityId: number
  cityName: string
  startsOn: string | null
  isLegacyImport: boolean
  endsOn: string | null
  createdSource?: 'official' | 'franchisee' | 'reused_by_franchisee'
  needsPedagogyReview?: boolean
  isSenior?: boolean
}

export interface FranchiseFormerPartnerAttention {
  partnerNumber: number
  cityId: number
  cityName: string
  partnerName: string
  activePartnerNames: Array<{ partnerNumber: number; partnerName: string }>
  hasActiveStudios: boolean
  actionableTeacherCount: number
  hideGroup: boolean
}

export interface TeacherTrainingCycle {
  trainingCycleId: string
  teacherId: string
  approvedVideoPresentationId: string | null
  isRepeatTraining: boolean
  isLegacyImport: boolean
  status: TeacherTrainingStatus
  startDate: string | null
  deadlineDate: string | null
  firstAttestationDate: string | null
  firstAttestationResult: TeacherAttestationResult | null
  secondAttestationDate: string | null
  secondAttestationResult: TeacherAttestationResult | null
  thirdAttestationDate: string | null
  thirdAttestationResult: TeacherAttestationResult | null
  completedAt: string | null
  closedAt: string | null
  closeReason: string | null
  createdAt: string
  updatedAt: string
}

export interface TeacherTrainingAttestationAttempt {
  attemptId: string
  trainingCycleId: string
  teacherId: string
  stage: 1 | 2 | 3
  attemptNo: number
  attestationDate: string | null
  result: TeacherAttestationResult
  isLegacyImport: boolean
  recordedBy: string | null
  createdAt: string
}

export interface TeacherContract {
  contractId: string
  teacherId: string
  trainingCycleId: string | null
  partnerNumber: number | null
  partnerName: string | null
  signedOn: string | null
  expiresOn: string | null
  contractUrl: string | null
  status: 'active' | 'replaced' | 'terminated'
}

export interface TeacherVideoPresentation {
  videoPresentationId: string
  teacherId: string
  partnerNumber: number | null
  partnerName: string | null
  cityId: number | null
  cityName: string | null
  videoUrl: string
  charismaScore: number | null
  professionalismScore: number | null
  clarityScore: number | null
  emotionalityScore: number | null
  averageScore: number | null
  decision: TeacherVideoDecision | null
  comment: string
  evaluatedAt: string | null
  usedByTraining: boolean
}

export interface TeacherAdditionalAttestation {
  additionalAttestationId: string
  teacherId: string
  attestationType: AdditionalAttestationType
  directionId: number | null
  directionName: string | null
  attestationDate: string | null
  isLegacyImport: boolean
  result: TeacherAttestationResult | null
  comment: string
}

export type TeacherReviewSource = 'management_company' | 'senior_teacher'

export interface TeacherReviewSummary {
  reviewId: string
  reviewDate: string
  trainingAt: string | null
  coachName: string
  cityName: string
  studioAddress: string
  directionName: string
  totalScore: number
  maxScore: number
  qualityPercent: number
  conclusion: string
  reviewSource: TeacherReviewSource
}

export interface TeacherStatusHistoryItem {
  statusHistoryId: string
  teacherId: string
  fromStatus: FranchiseTeacherStatus | null
  toStatus: FranchiseTeacherStatus
  reason: string
  changedAt: string
}

export type TeacherAttentionTone = 'error' | 'warning' | 'neutral'
export type TeacherAttentionKind = 'assignment' | 'video' | 'attestation' | 'training' | 'contract' | 'quality' | 'review'

export interface TeacherAttentionItem {
  key: string
  kind: TeacherAttentionKind
  label: string
  tone: TeacherAttentionTone
  priority: number
  stage?: 1 | 2 | 3
  dueDate?: string | null
}

export interface FranchiseTeacherListItem {
  teacherId: string
  fullName: string
  phoneNumber: string
  email: string
  telegramUsername: string
  qualification: TeacherQualification | null
  status: FranchiseTeacherStatus
  comment: string
  dismissedAt: string | null
  archivedAt: string | null
  canManage: boolean
  createdSource: 'official' | 'franchisee'
  needsPedagogyReview: boolean
  assignments: FranchiseTeacherAssignment[]
  formerPartnerAttention: FranchiseFormerPartnerAttention[]
  currentTraining: TeacherTrainingCycle | null
  hasPassedFirstAttestation: boolean
  activeContracts: TeacherContract[]
  nearestContractExpiry: string | null
  videoPresentationsCount: number
  latestVideoDecision: TeacherVideoDecision | null
  reviewsCount: number
  latestReviewDate: string | null
  averageQualityPercent: number | null
  attentionItems: TeacherAttentionItem[]
  createdAt: string
  updatedAt: string
  updatedBy: string | null
  updatedByName: string | null
}

export interface FranchiseTeacherDetail extends FranchiseTeacherListItem {
  assignmentsHistory: FranchiseTeacherAssignment[]
  trainingCycles: TeacherTrainingCycle[]
  attestationAttempts: TeacherTrainingAttestationAttempt[]
  contracts: TeacherContract[]
  videoPresentations: TeacherVideoPresentation[]
  additionalAttestations: TeacherAdditionalAttestation[]
  reviews: TeacherReviewSummary[]
  statusHistory: TeacherStatusHistoryItem[]
}

export type TeacherTrainingFilter = 'all' | 'active' | 'inactive'
export type TeacherVideoFilter = 'all' | 'missing' | 'pending' | TeacherVideoDecision
export type TeacherRegistryQuickFilter = 'all' | 'attention' | 'training' | 'attestation' | 'contract' | 'video' | 'quality' | 'review'

export interface FranchiseTeacherPageParams {
  search?: string
  employmentStatus?: 'all' | FranchiseTeacherStatus
  trainingState?: TeacherTrainingFilter
  videoStatus?: TeacherVideoFilter
  attentionKind?: 'all' | TeacherAttentionKind
  quickFilter?: TeacherRegistryQuickFilter
  specialistUserId?: string | null
  partnerNumber?: number | null
  cityId?: number | null
  includeArchived?: boolean
  limit?: number
  offset?: number
}

export interface PedagogyOperationalOverview {
  totalTeachers: number
  teachersRequiringAttention: number
  counts: Partial<Record<TeacherAttentionKind, number>>
  topItems: Array<{
    teacherId: string
    fullName: string
    status: FranchiseTeacherStatus
    attention: TeacherAttentionItem
  }>
}

export interface FranchiseTeacherPageResult {
  items: FranchiseTeacherListItem[]
  total: number
}

export interface TeacherPartnerGroup {
  partnerNumber: number
  partnerName: string
  activeTeachersCount: number
  activeTrainingCount: number
  attentionTeachersCount: number
}

export interface TeacherPartnerGroupsResult {
  groups: TeacherPartnerGroup[]
  uniqueTotal: number
}

export type TeacherRegistryQuickStats = Record<TeacherRegistryQuickFilter, number>

export interface FranchiseTeacherPartnerPageParams extends FranchiseTeacherPageParams {
  partnerNumber: number
}

export interface TeacherRegistryPartnerOption {
  partnerNumber: number
  partnerName: string
}

export interface TeacherRegistryCityOption {
  cityId: number
  cityName: string
}

export interface TeacherRegistryAssignmentOption {
  partnerNumber: number
  partnerName: string
  cityId: number
  cityName: string
}

export interface TeacherRegistryReferenceData {
  partners: TeacherRegistryPartnerOption[]
  cities: TeacherRegistryCityOption[]
  assignmentOptions: TeacherRegistryAssignmentOption[]
}

export type TeacherEntryMode = 'candidate' | 'active'

export interface CreateFranchiseTeacherAssignmentInput {
  partnerNumber: number
  cityId: number
  startsOn?: string | null
}

export interface CreateTeacherVideoPresentationInput {
  partnerNumber?: number | null
  cityId?: number | null
  videoUrl: string
  charismaScore?: number | null
  professionalismScore?: number | null
  clarityScore?: number | null
  emotionalityScore?: number | null
  decision?: TeacherVideoDecision | null
  comment?: string | null
}

export interface CreateTeacherContractInput {
  partnerNumber?: number | null
  signedOn: string
  contractUrl: string
}

export interface CreateFranchiseTeacherInput {
  entryMode?: TeacherEntryMode
  fullName: string
  phoneNumber?: string | null
  email?: string | null
  telegramUsername?: string | null
  qualification?: TeacherQualification | null
  comment?: string | null
  assignments: CreateFranchiseTeacherAssignmentInput[]
  videoPresentation?: CreateTeacherVideoPresentationInput | null
  contract?: CreateTeacherContractInput | null
}

export interface CreateFranchiseTeacherResult {
  teacherId: string
  status: FranchiseTeacherStatus
  videoPresentationId: string | null
  trainingCycleId: string | null
  contractId: string | null
}

export interface UpdateFranchiseTeacherInput {
  fullName: string
  phoneNumber?: string | null
  email?: string | null
  telegramUsername?: string | null
  qualification?: TeacherQualification | null
  comment?: string | null
}

export interface SyncFranchiseTeacherAssignmentInput {
  partnerNumber: number
  cityId: number
  startsOn?: string | null
}

export interface AddFranchiseTeacherAssignmentInput extends SyncFranchiseTeacherAssignmentInput {}

export interface StartTeacherTrainingInput {
  startDate: string
  isRepeat?: boolean
  approvedVideoPresentationId?: string | null
}

export interface SaveTeacherTrainingAttestationInput {
  trainingCycleId: string
  stage: 1 | 2 | 3
  attestationDate: string
  result: TeacherAttestationResult | null
}


export interface AddTeacherVideoPresentationInput {
  videoUrl: string
  charismaScore?: number | null
  professionalismScore?: number | null
  clarityScore?: number | null
  emotionalityScore?: number | null
  decision?: TeacherVideoDecision | null
  comment?: string | null
}

export type UpdateTeacherVideoPresentationInput = AddTeacherVideoPresentationInput

export interface AddTeacherAdditionalAttestationInput {
  attestationType: AdditionalAttestationType
  directionId?: number | null
  attestationDate: string
  result?: TeacherAttestationResult | null
  comment?: string | null
}

export type UpdateTeacherAdditionalAttestationInput = AddTeacherAdditionalAttestationInput

export interface AddTeacherContractInput {
  partnerNumber?: number | null
  signedOn: string
  contractUrl: string
}

export interface UpdateTeacherContractInput {
  partnerNumber?: number | null
  signedOn?: string | null
  contractUrl?: string | null
}

function formatError(error: unknown): string {
  if (!error) return 'Неизвестная ошибка'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const candidate = error as { message?: string; details?: string; hint?: string; code?: string }
    return [candidate.message, candidate.details, candidate.hint, candidate.code]
      .filter(Boolean)
      .join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function normalizeNullableText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function toNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function toNullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function mapAssignment(
  row: Record<string, unknown>,
  partnerNames: Map<number, string>,
  cityNames: Map<number, string>
): FranchiseTeacherAssignment {
  const partnerNumber = toNumber(row.partner_number)
  const cityId = toNumber(row.city_id)
  return {
    assignmentId: String(row.assignment_id),
    teacherId: String(row.teacher_id),
    partnerNumber,
    partnerName: normalizeText(row.partner_name) || partnerNames.get(partnerNumber) || `Франчайзи №${partnerNumber}`,
    cityId,
    cityName: normalizeText(row.city_name) || cityNames.get(cityId) || `Город №${cityId}`,
    startsOn: normalizeNullableText(row.starts_on),
    isLegacyImport: Boolean(row.is_legacy_import),
    endsOn: normalizeNullableText(row.ends_on),
    createdSource: row.created_source === 'franchisee'
      ? 'franchisee'
      : row.created_source === 'reused_by_franchisee'
        ? 'reused_by_franchisee'
        : 'official',
    needsPedagogyReview: Boolean(row.needs_review),
    isSenior: Boolean(row.is_senior),
  }
}

function mapFormerPartnerAttention(raw: unknown): FranchiseFormerPartnerAttention[] {
  return asRecordArray(raw).map(row => {
    const activePartnerNames = asRecordArray(row.active_partner_names).map(partner => ({
      partnerNumber: toNumber(partner.partner_number),
      partnerName: normalizeText(partner.partner_name),
    }))
    return {
      partnerNumber: toNumber(row.partner_number),
      cityId: toNumber(row.city_id),
      cityName: normalizeText(row.city_name),
      partnerName: normalizeText(row.partner_name),
      activePartnerNames,
      hasActiveStudios: Boolean(row.has_active_studios),
      actionableTeacherCount: toNumber(row.actionable_teacher_count),
      hideGroup: Boolean(row.hide_group),
    }
  })
}

function mapTrainingCycle(row: Record<string, unknown>): TeacherTrainingCycle {
  return {
    trainingCycleId: String(row.training_cycle_id),
    teacherId: String(row.teacher_id),
    approvedVideoPresentationId: row.approved_video_presentation_id == null
      ? null
      : String(row.approved_video_presentation_id),
    isRepeatTraining: Boolean(row.is_repeat_training),
    isLegacyImport: Boolean(row.is_legacy_import),
    status: row.status as TeacherTrainingStatus,
    startDate: normalizeNullableText(row.start_date),
    deadlineDate: normalizeNullableText(row.deadline_date),
    firstAttestationDate: normalizeNullableText(row.first_attestation_date),
    firstAttestationResult: (row.first_attestation_result as TeacherAttestationResult | null) ?? null,
    secondAttestationDate: normalizeNullableText(row.second_attestation_date),
    secondAttestationResult: (row.second_attestation_result as TeacherAttestationResult | null) ?? null,
    thirdAttestationDate: normalizeNullableText(row.third_attestation_date),
    thirdAttestationResult: (row.third_attestation_result as TeacherAttestationResult | null) ?? null,
    completedAt: normalizeNullableText(row.completed_at),
    closedAt: normalizeNullableText(row.closed_at),
    closeReason: normalizeNullableText(row.close_reason),
    createdAt: normalizeText(row.created_at),
    updatedAt: normalizeText(row.updated_at),
  }
}

function mapAttestationAttempt(row: Record<string, unknown>): TeacherTrainingAttestationAttempt {
  return {
    attemptId: String(row.attempt_id),
    trainingCycleId: String(row.training_cycle_id),
    teacherId: String(row.teacher_id),
    stage: Number(row.stage) as 1 | 2 | 3,
    attemptNo: toNumber(row.attempt_no, 1),
    attestationDate: normalizeNullableText(row.attestation_date),
    result: row.result as TeacherAttestationResult,
    isLegacyImport: Boolean(row.is_legacy_import),
    recordedBy: row.recorded_by == null ? null : String(row.recorded_by),
    createdAt: normalizeText(row.created_at),
  }
}

function mapContract(
  row: Record<string, unknown>,
  partnerNames: Map<number, string>
): TeacherContract {
  const partnerNumber = row.partner_number == null ? null : toNumber(row.partner_number)
  return {
    contractId: String(row.contract_id),
    teacherId: String(row.teacher_id),
    trainingCycleId: row.training_cycle_id == null ? null : String(row.training_cycle_id),
    partnerNumber,
    partnerName: partnerNumber == null
      ? null
      : (normalizeText(row.partner_name) || partnerNames.get(partnerNumber) || `Франчайзи №${partnerNumber}`),
    signedOn: normalizeNullableText(row.signed_on),
    expiresOn: normalizeNullableText(row.expires_on),
    contractUrl: normalizeNullableText(row.contract_url),
    status: row.status as TeacherContract['status'],
  }
}

function mapVideoPresentation(
  row: Record<string, unknown>,
  partnerNames: Map<number, string>,
  cityNames: Map<number, string>
): TeacherVideoPresentation {
  const partnerNumber = row.partner_number == null ? null : toNumber(row.partner_number)
  const cityId = row.city_id == null ? null : toNumber(row.city_id)
  return {
    videoPresentationId: String(row.video_presentation_id),
    teacherId: String(row.teacher_id),
    partnerNumber,
    partnerName: partnerNumber == null
      ? null
      : (normalizeText(row.partner_name) || partnerNames.get(partnerNumber) || `Франчайзи №${partnerNumber}`),
    cityId,
    cityName: cityId == null
      ? null
      : (normalizeText(row.city_name) || cityNames.get(cityId) || `Город №${cityId}`),
    videoUrl: normalizeText(row.video_url),
    charismaScore: toNullableNumber(row.charisma_score),
    professionalismScore: toNullableNumber(row.professionalism_score),
    clarityScore: toNullableNumber(row.clarity_score),
    emotionalityScore: toNullableNumber(row.emotionality_score),
    averageScore: toNullableNumber(row.average_score),
    decision: (row.decision as TeacherVideoDecision | null) ?? null,
    comment: normalizeText(row.comment),
    evaluatedAt: normalizeNullableText(row.evaluated_at),
    usedByTraining: Boolean(row.used_by_training),
  }
}

function mapAdditionalAttestation(
  row: Record<string, unknown>,
  directionNames: Map<number, string>
): TeacherAdditionalAttestation {
  const directionId = row.direction_id == null ? null : toNumber(row.direction_id)
  return {
    additionalAttestationId: String(row.additional_attestation_id),
    teacherId: String(row.teacher_id),
    attestationType: row.attestation_type as AdditionalAttestationType,
    directionId,
    directionName: directionId == null
      ? null
      : (normalizeText(row.direction_name) || directionNames.get(directionId) || `Направление №${directionId}`),
    attestationDate: normalizeNullableText(row.attestation_date),
    isLegacyImport: Boolean(row.is_legacy_import),
    result: (row.result as TeacherAttestationResult | null) ?? null,
    comment: normalizeText(row.comment),
  }
}

function mapStatusHistory(row: Record<string, unknown>): TeacherStatusHistoryItem {
  return {
    statusHistoryId: String(row.status_history_id),
    teacherId: String(row.teacher_id),
    fromStatus: (row.from_status as FranchiseTeacherStatus | null) ?? null,
    toStatus: row.to_status as FranchiseTeacherStatus,
    reason: normalizeText(row.reason),
    changedAt: normalizeText(row.changed_at),
  }
}

function mapReview(row: Record<string, unknown>, directionNames: Map<number, string>): TeacherReviewSummary {
  const rawDirectionId = row.directionId ?? row.direction_id
  const directionId = rawDirectionId == null ? null : toNumber(rawDirectionId)
  const providedDirectionName = normalizeText(row.directionName ?? row.direction_name)
  return {
    reviewId: String(row.reviewId ?? row.review_id),
    reviewDate: normalizeText(row.reviewDate ?? row.review_date),
    trainingAt: normalizeNullableText(row.trainingAt ?? row.training_at),
    coachName: normalizeText(row.coachName ?? row.coach_name),
    cityName: normalizeText(row.cityName ?? row.city_name),
    studioAddress: normalizeText(row.studioAddress ?? row.studio_address),
    directionName: providedDirectionName || (directionId == null
      ? ''
      : (directionNames.get(directionId) ?? `Направление №${directionId}`)),
    totalScore: toNumber(row.totalScore ?? row.total_score),
    maxScore: toNumber(row.maxScore ?? row.max_score),
    qualityPercent: toNumber(row.qualityPercent ?? row.quality_percent),
    conclusion: normalizeText(row.conclusion),
    reviewSource: row.reviewSource === 'senior_teacher' || row.review_source === 'senior_teacher'
      ? 'senior_teacher'
      : 'management_company',
  }
}

function asRecordArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter(item => item && typeof item === 'object') as Array<Record<string, unknown>> : []
}

function mapAttentionItem(row: Record<string, unknown>): TeacherAttentionItem | null {
  const kind = normalizeText(row.kind) as TeacherAttentionKind
  const tone = normalizeText(row.tone) as TeacherAttentionTone
  if (!['assignment', 'video', 'attestation', 'training', 'contract', 'quality', 'review'].includes(kind)) return null
  if (!['error', 'warning', 'neutral'].includes(tone)) return null
  const stageNumber = toNumber(row.stage, 0)
  return {
    key: normalizeText(row.key),
    kind,
    label: normalizeText(row.label),
    tone,
    priority: toNumber(row.priority),
    ...(stageNumber >= 1 && stageNumber <= 3 ? { stage: stageNumber as 1 | 2 | 3 } : {}),
    ...(row.dueDate != null || row.due_date != null
      ? { dueDate: normalizeNullableText(row.dueDate ?? row.due_date) }
      : {}),
  }
}

function mapAttentionItems(value: unknown): TeacherAttentionItem[] {
  return asRecordArray(value)
    .map(mapAttentionItem)
    .filter((item): item is TeacherAttentionItem => item != null)
}

function mapTeacherPageRow(raw: Record<string, unknown>): FranchiseTeacherListItem {
  const emptyPartners = new Map<number, string>()
  const emptyCities = new Map<number, string>()
  const assignments = asRecordArray(raw.assignments).map(row => mapAssignment(row, emptyPartners, emptyCities))
  const activeContracts = asRecordArray(raw.active_contracts).map(row => mapContract(row, emptyPartners))
  const trainingRaw = raw.current_training && typeof raw.current_training === 'object'
    ? raw.current_training as Record<string, unknown>
    : null

  return {
    teacherId: String(raw.teacher_id),
    fullName: normalizeText(raw.full_name),
    phoneNumber: normalizeText(raw.phone_number),
    email: normalizeText(raw.email),
    telegramUsername: normalizeText(raw.telegram_username),
    qualification: (raw.qualification as TeacherQualification | null) ?? null,
    status: raw.status as FranchiseTeacherStatus,
    comment: normalizeText(raw.comment),
    dismissedAt: normalizeNullableText(raw.dismissed_at),
    archivedAt: normalizeNullableText(raw.archived_at),
    canManage: raw.can_manage == null ? true : Boolean(raw.can_manage),
    createdSource: raw.created_source === 'franchisee' ? 'franchisee' : 'official',
    needsPedagogyReview: Boolean(raw.needs_pedagogy_review),
    assignments,
    formerPartnerAttention: mapFormerPartnerAttention(raw.former_partner_attention),
    currentTraining: trainingRaw ? mapTrainingCycle(trainingRaw) : null,
    hasPassedFirstAttestation: Boolean(raw.has_passed_first_attestation),
    activeContracts,
    nearestContractExpiry: normalizeNullableText(raw.nearest_contract_expiry),
    videoPresentationsCount: toNumber(raw.video_presentations_count),
    latestVideoDecision: (raw.latest_video_decision as TeacherVideoDecision | null) ?? null,
    reviewsCount: toNumber(raw.reviews_count),
    latestReviewDate: normalizeNullableText(raw.latest_review_date),
    averageQualityPercent: toNullableNumber(raw.average_quality_percent),
    attentionItems: mapAttentionItems(raw.attention_items),
    createdAt: normalizeText(raw.created_at),
    updatedAt: normalizeText(raw.updated_at),
    updatedBy: normalizeNullableText(raw.updated_by),
    updatedByName: normalizeNullableText(raw.updated_by_name),
  }
}

async function listTeacherAppFlags(teacherIds: string[]): Promise<Map<string, { createdSource: 'official' | 'franchisee'; needsPedagogyReview: boolean }>> {
  if (teacherIds.length === 0) return new Map()
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_teacher_app_flags', { p_teacher_ids: teacherIds })
  )
  if (error) throw new Error(formatError(error))
  return new Map(((data ?? []) as Array<Record<string, unknown>>).map(row => [
    normalizeText(row.teacher_id),
    {
      createdSource: row.created_source === 'franchisee' ? 'franchisee' : 'official',
      needsPedagogyReview: Boolean(row.needs_pedagogy_review),
    },
  ]))
}

export async function listFranchiseTeachersPage(
  params: FranchiseTeacherPageParams = {}
): Promise<FranchiseTeacherPageResult> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_teachers_page_v2', {
      p_search: params.search?.trim() || null,
      p_employment_status: !params.employmentStatus || params.employmentStatus === 'all' ? null : params.employmentStatus,
      p_training_state: !params.trainingState || params.trainingState === 'all' ? null : params.trainingState,
      p_video_status: !params.videoStatus || params.videoStatus === 'all' ? null : params.videoStatus,
      p_attention_kind: !params.attentionKind || params.attentionKind === 'all' ? null : params.attentionKind,
      p_quick_filter: !params.quickFilter || params.quickFilter === 'all' ? null : params.quickFilter,
      p_specialist_user_id: params.specialistUserId || null,
      p_partner_number: params.partnerNumber ?? null,
      p_city_id: params.cityId ?? null,
      p_include_archived: Boolean(params.includeArchived),
      p_limit: params.limit ?? 100,
      p_offset: params.offset ?? 0,
    })
  )

  if (error) throw new Error(formatError(error))
  const rows = (data ?? []) as Array<Record<string, unknown>>
  const items = rows.map(row => mapTeacherPageRow(row))
  if (items.length === 0) {
    return { items, total: 0 }
  }

  const { data: attentionData, error: attentionError } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_former_partner_attention', {
      p_teacher_ids: items.map(item => item.teacherId),
    })
  )
  if (attentionError) throw new Error(formatError(attentionError))

  const attentionByTeacher = new Map<string, FranchiseFormerPartnerAttention[]>()
  for (const row of (attentionData ?? []) as Array<Record<string, unknown>>) {
    const attention = mapFormerPartnerAttention([row])
    const teacherId = normalizeText(row.teacher_id)
    if (!teacherId || attention.length === 0) continue
    const existing = attentionByTeacher.get(teacherId) ?? []
    attentionByTeacher.set(teacherId, [...existing, ...attention])
  }

  const appFlags = await listTeacherAppFlags(items.map(item => item.teacherId))

  return {
    items: items.map(item => ({
      ...item,
      ...(appFlags.get(item.teacherId) ?? {}),
      formerPartnerAttention: attentionByTeacher.get(item.teacherId) ?? [],
    })),
    total: rows.length > 0 ? toNumber(rows[0].total_count, rows.length) : 0,
  }
}

export async function listTeacherPartnerGroups(
  params: FranchiseTeacherPageParams = {}
): Promise<TeacherPartnerGroupsResult> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_teacher_partner_groups', {
      p_search: params.search?.trim() || null,
      p_employment_status: !params.employmentStatus || params.employmentStatus === 'all' ? null : params.employmentStatus,
      p_training_state: !params.trainingState || params.trainingState === 'all' ? null : params.trainingState,
      p_video_status: !params.videoStatus || params.videoStatus === 'all' ? null : params.videoStatus,
      p_attention_kind: !params.attentionKind || params.attentionKind === 'all' ? null : params.attentionKind,
      p_specialist_user_id: params.specialistUserId || null,
      p_partner_number: params.partnerNumber ?? null,
      p_city_id: params.cityId ?? null,
      p_include_archived: Boolean(params.includeArchived),
      p_quick_filter: !params.quickFilter || params.quickFilter === 'all' ? null : params.quickFilter,
    })
  )

  if (error) throw new Error(formatError(error))
  const rows = (data ?? []) as Array<Record<string, unknown>>
  return {
    groups: rows.map(row => ({
      partnerNumber: toNumber(row.partner_number),
      partnerName: normalizeText(row.partner_name) || `Франчайзи №${toNumber(row.partner_number)}`,
      activeTeachersCount: toNumber(row.active_teachers_count),
      activeTrainingCount: toNumber(row.active_training_count),
      attentionTeachersCount: toNumber(row.attention_teachers_count),
    })),
    uniqueTotal: rows.length > 0 ? toNumber(rows[0].total_unique_teachers) : 0,
  }
}

export async function getTeacherRegistryQuickStats(
  params: FranchiseTeacherPageParams = {}
): Promise<TeacherRegistryQuickStats> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_teacher_registry_quick_stats', {
      p_search: params.search?.trim() || null,
      p_employment_status: !params.employmentStatus || params.employmentStatus === 'all' ? null : params.employmentStatus,
      p_training_state: !params.trainingState || params.trainingState === 'all' ? null : params.trainingState,
      p_video_status: !params.videoStatus || params.videoStatus === 'all' ? null : params.videoStatus,
      p_attention_kind: !params.attentionKind || params.attentionKind === 'all' ? null : params.attentionKind,
      p_specialist_user_id: params.specialistUserId || null,
      p_partner_number: params.partnerNumber ?? null,
      p_city_id: params.cityId ?? null,
      p_include_archived: Boolean(params.includeArchived),
    })
  )

  if (error) throw new Error(formatError(error))
  const stats: TeacherRegistryQuickStats = {
    all: 0,
    attention: 0,
    training: 0,
    attestation: 0,
    contract: 0,
    video: 0,
    quality: 0,
    review: 0,
  }
  for (const row of (data ?? []) as Array<Record<string, unknown>>) {
    const key = normalizeText(row.metric_key) as TeacherRegistryQuickFilter
    if (key in stats) stats[key] = toNumber(row.teacher_count)
  }
  return stats
}

export async function listFranchiseTeachersForPartnerPage(
  params: FranchiseTeacherPartnerPageParams
): Promise<FranchiseTeacherPageResult> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_teachers_for_partner_page', {
      p_partner_number: params.partnerNumber,
      p_search: params.search?.trim() || null,
      p_employment_status: !params.employmentStatus || params.employmentStatus === 'all' ? null : params.employmentStatus,
      p_training_state: !params.trainingState || params.trainingState === 'all' ? null : params.trainingState,
      p_video_status: !params.videoStatus || params.videoStatus === 'all' ? null : params.videoStatus,
      p_attention_kind: !params.attentionKind || params.attentionKind === 'all' ? null : params.attentionKind,
      p_specialist_user_id: params.specialistUserId || null,
      p_city_id: params.cityId ?? null,
      p_include_archived: Boolean(params.includeArchived),
      p_limit: params.limit ?? 50,
      p_offset: params.offset ?? 0,
      p_quick_filter: !params.quickFilter || params.quickFilter === 'all' ? null : params.quickFilter,
    })
  )

  if (error) throw new Error(formatError(error))
  const rows = (data ?? []) as Array<Record<string, unknown>>
  const mapped = rows.map(row => mapTeacherPageRow(row))
  return {
    items: mapped,
    total: rows.length > 0 ? toNumber(rows[0].total_count, rows.length) : 0,
  }
}

export async function listFranchiseTeachers(): Promise<FranchiseTeacherListItem[]> {
  const items: FranchiseTeacherListItem[] = []
  const pageSize = 500
  let offset = 0

  let hasMore = true
  while (hasMore) {
    const page = await listFranchiseTeachersPage({ limit: pageSize, offset })
    items.push(...page.items)
    hasMore = items.length < page.total && page.items.length >= pageSize
    offset += page.items.length
  }

  return items
}

export async function getPedagogyOperationalOverview(limit = 12): Promise<PedagogyOperationalOverview> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_operational_overview', { p_limit: limit })
  )
  if (error) throw new Error(formatError(error))

  const raw = data ?? {}
  const countsRaw = raw.counts && typeof raw.counts === 'object'
    ? raw.counts as Record<string, unknown>
    : {}
  const counts: PedagogyOperationalOverview['counts'] = {}
  for (const kind of ['assignment', 'video', 'attestation', 'training', 'contract', 'quality', 'review'] as TeacherAttentionKind[]) {
    const count = toNumber(countsRaw[kind], 0)
    if (count > 0) counts[kind] = count
  }

  const topItems = asRecordArray(raw.topItems ?? raw.top_items).flatMap(item => {
    const attentionRaw = item.attention && typeof item.attention === 'object'
      ? item.attention as Record<string, unknown>
      : null
    const attention = attentionRaw ? mapAttentionItem(attentionRaw) : null
    if (!attention) return []
    return [{
      teacherId: normalizeText(item.teacherId ?? item.teacher_id),
      fullName: normalizeText(item.fullName ?? item.full_name),
      status: item.status as FranchiseTeacherStatus,
      attention,
    }]
  })

  return {
    totalTeachers: toNumber(raw.totalTeachers ?? raw.total_teachers),
    teachersRequiringAttention: toNumber(raw.teachersRequiringAttention ?? raw.teachers_requiring_attention),
    counts,
    topItems,
  }
}

export async function getFranchiseTeacherDetail(teacherId: string): Promise<FranchiseTeacherDetail | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_teacher_detail', { p_teacher_id: teacherId })
  )
  if (error) throw new Error(formatError(error))
  if (!data) return null

  const raw = data
  const emptyPartners = new Map<number, string>()
  const emptyCities = new Map<number, string>()
  const emptyDirections = new Map<number, string>()
  const assignmentsHistory = asRecordArray(raw.assignments_history)
    .map(row => mapAssignment(row, emptyPartners, emptyCities))
  const assignments = asRecordArray(raw.assignments)
    .map(row => mapAssignment(row, emptyPartners, emptyCities))
  const trainingCycles = asRecordArray(raw.training_cycles).map(mapTrainingCycle)
  const currentTrainingRaw = raw.current_training && typeof raw.current_training === 'object'
    ? raw.current_training as Record<string, unknown>
    : null
  const attestationAttempts = asRecordArray(raw.attestation_attempts).map(mapAttestationAttempt)
  const contracts = asRecordArray(raw.contracts).map(row => mapContract(row, emptyPartners))
  const activeContracts = asRecordArray(raw.active_contracts).map(row => mapContract(row, emptyPartners))
  const videoPresentations = asRecordArray(raw.video_presentations)
    .map(row => mapVideoPresentation(row, emptyPartners, emptyCities))
  const additionalAttestations = asRecordArray(raw.additional_attestations)
    .map(row => mapAdditionalAttestation(row, emptyDirections))
  const reviews = asRecordArray(raw.reviews).map(row => mapReview(row, emptyDirections))
  const statusHistory = asRecordArray(raw.status_history).map(mapStatusHistory)
  const nearestContractExpiry = activeContracts.length > 0
    ? activeContracts.map(item => item.expiresOn).filter((value): value is string => Boolean(value)).sort()[0] ?? null
    : null

  const detail: FranchiseTeacherDetail = {
    teacherId: normalizeText(raw.teacher_id) || teacherId,
    fullName: normalizeText(raw.full_name),
    phoneNumber: normalizeText(raw.phone_number),
    email: normalizeText(raw.email),
    telegramUsername: normalizeText(raw.telegram_username),
    qualification: (raw.qualification as TeacherQualification | null) ?? null,
    status: raw.status as FranchiseTeacherStatus,
    comment: normalizeText(raw.comment),
    dismissedAt: normalizeNullableText(raw.dismissed_at),
    archivedAt: normalizeNullableText(raw.archived_at),
    canManage: raw.can_manage == null ? true : Boolean(raw.can_manage),
    createdSource: raw.created_source === 'franchisee' ? 'franchisee' : 'official',
    needsPedagogyReview: Boolean(raw.needs_pedagogy_review),
    assignments,
    assignmentsHistory,
    // Карточку отдаёт своя RPC, признак «бывший франчайзи» приходит отдельной
    // ручкой get_pedagogy_former_partner_attention — маппер вернёт [], если поля нет.
    formerPartnerAttention: mapFormerPartnerAttention(raw.former_partner_attention),
    currentTraining: currentTrainingRaw ? mapTrainingCycle(currentTrainingRaw) : null,
    hasPassedFirstAttestation: Boolean(raw.has_passed_first_attestation),
    trainingCycles,
    attestationAttempts,
    activeContracts,
    nearestContractExpiry,
    contracts,
    videoPresentations,
    videoPresentationsCount: toNumber(raw.video_presentations_count, videoPresentations.length),
    latestVideoDecision: (raw.latest_video_decision as TeacherVideoDecision | null) ?? null,
    additionalAttestations,
    reviews,
    statusHistory,
    reviewsCount: toNumber(raw.reviews_count, reviews.length),
    latestReviewDate: normalizeNullableText(raw.latest_review_date),
    averageQualityPercent: toNullableNumber(raw.average_quality_percent),
    attentionItems: mapAttentionItems(raw.attention_items),
    createdAt: normalizeText(raw.created_at),
    updatedAt: normalizeText(raw.updated_at),
    updatedBy: normalizeNullableText(raw.updated_by),
    updatedByName: normalizeNullableText(raw.updated_by_name),
  }

  const appFlags = await listTeacherAppFlags([teacherId])
  return {
    ...detail,
    ...(appFlags.get(teacherId) ?? {}),
  }
}

export async function updateFranchiseTeacher(
  teacherId: string,
  input: UpdateFranchiseTeacherInput
): Promise<void> {
  const fullName = input.fullName.trim()
  if (!fullName) throw new Error('Укажите ФИО педагога')

  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('update_franchise_teacher_profile', {
      p_teacher_id: teacherId,
      p_full_name: fullName,
      p_phone_number: input.phoneNumber?.trim() || null,
      p_email: input.email?.trim().toLowerCase() || null,
      p_telegram_username: input.telegramUsername?.trim().replace(/^@+/, '') || null,
      p_qualification: input.qualification ?? null,
      p_comment: input.comment?.trim() || null,
    })
  )

  if (error) throw new Error(formatError(error))
}

export async function archiveFranchiseTeacher(teacherId: string, reason?: string | null): Promise<void> {
  if (!reason?.trim()) throw new Error('Укажите причину архивации карточки')
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('archive_franchise_teacher', {
      p_teacher_id: teacherId,
      p_reason: reason?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function restoreArchivedFranchiseTeacher(teacherId: string, reason: string): Promise<void> {
  if (!reason.trim()) throw new Error('Укажите причину восстановления карточки')
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('restore_archived_franchise_teacher', {
      p_teacher_id: teacherId,
      p_reason: reason.trim(),
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function listTeacherRegistryReferenceData(): Promise<TeacherRegistryReferenceData> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_teacher_registry_assignment_options')
  )

  if (error) throw new Error(formatError(error))

  const assignmentOptions = ((data ?? []) as Array<Record<string, unknown>>)
    .map(row => ({
      partnerNumber: toNumber(row.partner_number, Number.NaN),
      partnerName: normalizeText(row.partner_name),
      cityId: toNumber(row.city_id, Number.NaN),
      cityName: normalizeText(row.city_name),
    }))
    .filter(item => Number.isFinite(item.partnerNumber) && Number.isFinite(item.cityId))

  const partnerMap = new Map<number, string>()
  const cityMap = new Map<number, string>()
  for (const option of assignmentOptions) {
    partnerMap.set(option.partnerNumber, option.partnerName || `Франчайзи №${option.partnerNumber}`)
    cityMap.set(option.cityId, option.cityName || `Город №${option.cityId}`)
  }

  return {
    partners: Array.from(partnerMap.entries())
      .map(([partnerNumber, partnerName]) => ({ partnerNumber, partnerName }))
      .sort((a, b) => a.partnerName.localeCompare(b.partnerName, 'ru')),
    cities: Array.from(cityMap.entries())
      .map(([cityId, cityName]) => ({ cityId, cityName }))
      .sort((a, b) => a.cityName.localeCompare(b.cityName, 'ru')),
    assignmentOptions,
  }
}

export type FranchiseTeacherContactMatch = {
  teacherId: string
  matchKind: 'email' | 'phone'
  fullName: string
  status: FranchiseTeacherStatus
  statusLabel: string
  archived: boolean
  assignmentLabel: string | null
  canManage: boolean
}

/**
 * Кому принадлежит уже занятая почта или телефон. Правило совпадения общее с
 * гейтом дублей в create_franchise_teacher_workflow (обе стороны зовут
 * franchise_teacher_contact_matches), поэтому подсказка формы не может
 * разойтись с отказом сервера.
 */
export async function findFranchiseTeacherByContact(params: {
  email?: string | null
  phoneNumber?: string | null
}): Promise<FranchiseTeacherContactMatch[]> {
  const email = params.email?.trim().toLowerCase() || null
  const phoneNumber = params.phoneNumber?.trim() || null
  if (!email && !phoneNumber) return []

  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('find_franchise_teacher_by_contact', {
      p_email: email,
      p_phone_number: phoneNumber,
    })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    teacherId: normalizeText(row.teacher_id),
    matchKind: normalizeText(row.match_kind) === 'phone' ? 'phone' : 'email',
    fullName: normalizeText(row.full_name),
    status: normalizeText(row.status) as FranchiseTeacherStatus,
    statusLabel: normalizeText(row.status_label),
    archived: Boolean(row.archived_at),
    assignmentLabel: normalizeNullableText(row.assignment_label),
    canManage: row.can_manage === true,
  }))
}

export async function createFranchiseTeacher(
  input: CreateFranchiseTeacherInput
): Promise<CreateFranchiseTeacherResult> {
  const fullName = input.fullName.trim()
  if (!fullName) throw new Error('Укажите ФИО педагога')
  if (input.assignments.length === 0) {
    throw new Error('Добавьте хотя бы одну связь с франчайзи и городом')
  }
  if (input.assignments.some(assignment => !assignment.startsOn)) {
    throw new Error('Укажите дату начала для каждой новой связи')
  }

  const supabase = getSupabase()
  const payload = {
    entryMode: input.entryMode ?? 'candidate',
    fullName,
    phoneNumber: input.phoneNumber?.trim() || null,
    email: input.email?.trim().toLowerCase() || null,
    telegramUsername: input.telegramUsername?.trim().replace(/^@+/, '') || null,
    qualification: input.qualification ?? null,
    comment: input.comment?.trim() || null,
    assignments: input.assignments.map(assignment => ({
      partnerNumber: assignment.partnerNumber,
      cityId: assignment.cityId,
      startsOn: assignment.startsOn || null,
    })),
    videoPresentation: input.videoPresentation
      ? {
          ...input.videoPresentation,
          partnerNumber: input.videoPresentation.partnerNumber ?? null,
          cityId: input.videoPresentation.cityId ?? null,
          videoUrl: input.videoPresentation.videoUrl.trim(),
          charismaScore: input.videoPresentation.charismaScore ?? null,
          professionalismScore: input.videoPresentation.professionalismScore ?? null,
          clarityScore: input.videoPresentation.clarityScore ?? null,
          emotionalityScore: input.videoPresentation.emotionalityScore ?? null,
          decision: input.videoPresentation.decision ?? null,
          comment: input.videoPresentation.comment?.trim() || null,
        }
      : null,
    contract: input.contract
      ? {
          partnerNumber: input.contract.partnerNumber ?? null,
          signedOn: input.contract.signedOn,
          contractUrl: input.contract.contractUrl.trim(),
        }
      : null,
  }

  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('create_franchise_teacher_workflow', {
      p_payload: payload,
    })
  )

  if (error) throw new Error(formatError(error))

  const result = (data ?? {}) as Record<string, unknown>
  const teacherId = normalizeText(result.teacherId)
  if (!teacherId) throw new Error('Сервер не вернул идентификатор созданного педагога')

  return {
    teacherId,
    status: result.status as FranchiseTeacherStatus,
    videoPresentationId: normalizeNullableText(result.videoPresentationId),
    trainingCycleId: normalizeNullableText(result.trainingCycleId),
    contractId: normalizeNullableText(result.contractId),
  }
}


export async function syncFranchiseTeacherAssignments(
  teacherId: string,
  assignments: SyncFranchiseTeacherAssignmentInput[]
): Promise<void> {
  if (assignments.length === 0) throw new Error('Добавьте хотя бы одну связь')
  if (assignments.some(assignment => !assignment.startsOn)) {
    throw new Error('Укажите дату начала для каждой новой связи')
  }
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('sync_franchise_teacher_assignments', {
      p_teacher_id: teacherId,
      p_assignments: assignments.map(item => ({
        partnerNumber: item.partnerNumber,
        cityId: item.cityId,
        startsOn: item.startsOn || null,
      })),
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function updateFranchiseTeacherAssignment(
  assignmentId: string,
  input: Pick<SyncFranchiseTeacherAssignmentInput, 'startsOn'>
): Promise<void> {
  if (!input.startsOn) throw new Error('Укажите дату начала связи')
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('update_franchise_teacher_assignment_start_date', {
      p_assignment_id: assignmentId,
      p_starts_on: input.startsOn,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function addFranchiseTeacherAssignment(
  teacherId: string,
  input: AddFranchiseTeacherAssignmentInput
): Promise<string> {
  if (!input.startsOn) throw new Error('Укажите дату начала связи')
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('add_franchise_teacher_assignment', {
      p_teacher_id: teacherId,
      p_partner_number: input.partnerNumber,
      p_city_id: input.cityId,
      p_starts_on: input.startsOn,
    })
  )
  if (error) throw new Error(formatError(error))
  return String(data)
}

export async function deleteFranchiseTeacherAssignment(assignmentId: string, reason: string): Promise<void> {
  if (!reason.trim()) throw new Error('Укажите причину удаления ошибочной связи')
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('delete_franchise_teacher_assignment', {
      p_assignment_id: assignmentId,
      p_reason: reason.trim(),
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function dismissFranchiseTeacherFromPartner(
  teacherId: string,
  partnerNumber: number,
  reason: string
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('dismiss_franchise_teacher_from_partner', {
      p_teacher_id: teacherId,
      p_partner_number: partnerNumber,
      p_reason: reason.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function changeFranchiseTeacherStatus(
  teacherId: string,
  status: FranchiseTeacherStatus,
  reason?: string | null
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('change_franchise_teacher_status', {
      p_teacher_id: teacherId,
      p_status: status,
      p_reason: reason?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function startFranchiseTeacherTraining(
  teacherId: string,
  input: StartTeacherTrainingInput
): Promise<string> {
  if (!input.startDate) throw new Error('Укажите дату начала обучения')
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('start_franchise_teacher_training', {
      p_teacher_id: teacherId,
      p_start_date: input.startDate,
      p_is_repeat: Boolean(input.isRepeat),
      p_approved_video_presentation_id: input.approvedVideoPresentationId || null,
      p_is_legacy_import: false,
    })
  )
  if (error) throw new Error(formatError(error))
  return String(data)
}

export async function overrideUsedTeacherVideoDecision(
  videoPresentationId: string,
  decision: TeacherVideoDecision | null,
  reason: string
): Promise<void> {
  if (!reason.trim()) throw new Error('Укажите причину изменения решения')
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('override_used_teacher_video_decision', {
      p_video_presentation_id: videoPresentationId,
      p_decision: decision,
      p_reason: reason.trim(),
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function saveTeacherTrainingAttestation(
  input: SaveTeacherTrainingAttestationInput
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('save_teacher_training_attestation', {
      p_training_cycle_id: input.trainingCycleId,
      p_stage: input.stage,
      p_attestation_date: input.attestationDate,
      p_result: input.result,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function closeTeacherTrainingCycle(
  trainingCycleId: string,
  reason: string
): Promise<void> {
  if (!reason.trim()) throw new Error('Укажите причину закрытия обучения')
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('close_teacher_training_cycle', {
      p_training_cycle_id: trainingCycleId,
      p_reason: reason.trim(),
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function addFranchiseTeacherContract(
  teacherId: string,
  input: AddTeacherContractInput
): Promise<string> {
  const contractUrl = input.contractUrl.trim()
  if (!contractUrl) throw new Error('Укажите ссылку на договор')
  if (input.partnerNumber == null) throw new Error('Выберите франчайзи для договора')
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('add_franchise_teacher_contract', {
      p_teacher_id: teacherId,
      p_partner_number: input.partnerNumber ?? null,
      p_signed_on: input.signedOn,
      p_contract_url: contractUrl,
      p_replace_active: true,
    })
  )
  if (error) throw new Error(formatError(error))
  return String(data)
}

export async function setFranchiseTeacherContractStatus(
  contractId: string,
  status: TeacherContract['status']
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('set_franchise_teacher_contract_status', {
      p_contract_id: contractId,
      p_status: status,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function updateFranchiseTeacherContract(
  contractId: string,
  input: UpdateTeacherContractInput
): Promise<void> {
  const contractUrl = input.contractUrl?.trim() || null
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('update_franchise_teacher_contract', {
      p_contract_id: contractId,
      p_partner_number: input.partnerNumber ?? null,
      p_signed_on: input.signedOn,
      p_contract_url: contractUrl,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function addTeacherVideoPresentation(
  teacherId: string,
  input: AddTeacherVideoPresentationInput
): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('add_franchise_teacher_video_presentation', {
      p_teacher_id: teacherId,
      p_video_url: input.videoUrl.trim(),
      p_charisma_score: input.charismaScore ?? null,
      p_professionalism_score: input.professionalismScore ?? null,
      p_clarity_score: input.clarityScore ?? null,
      p_emotionality_score: input.emotionalityScore ?? null,
      p_decision: input.decision ?? null,
      p_comment: input.comment?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
  return String(data)
}

export async function updateTeacherVideoPresentation(
  videoPresentationId: string,
  input: UpdateTeacherVideoPresentationInput
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('update_franchise_teacher_video_presentation', {
      p_video_presentation_id: videoPresentationId,
      p_video_url: input.videoUrl.trim(),
      p_charisma_score: input.charismaScore ?? null,
      p_professionalism_score: input.professionalismScore ?? null,
      p_clarity_score: input.clarityScore ?? null,
      p_emotionality_score: input.emotionalityScore ?? null,
      p_decision: input.decision ?? null,
      p_comment: input.comment?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
}

export async function addTeacherAdditionalAttestation(
  teacherId: string,
  input: AddTeacherAdditionalAttestationInput
): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('add_teacher_additional_attestation', {
      p_teacher_id: teacherId,
      p_attestation_type: input.attestationType,
      p_direction_id: input.directionId ?? null,
      p_attestation_date: input.attestationDate,
      p_result: input.result ?? null,
      p_comment: input.comment?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
  return String(data)
}

export async function updateTeacherAdditionalAttestation(
  additionalAttestationId: string,
  input: UpdateTeacherAdditionalAttestationInput
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('update_teacher_additional_attestation', {
      p_additional_attestation_id: additionalAttestationId,
      p_attestation_type: input.attestationType,
      p_direction_id: input.directionId ?? null,
      p_attestation_date: input.attestationDate,
      p_result: input.result ?? null,
      p_comment: input.comment?.trim() || null,
    })
  )
  if (error) throw new Error(formatError(error))
}
